/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useMemo } from 'react';
import { useQuery } from 'react-apollo';

import { usePageContentId } from '@confluence/page-context';
import { ContentStatus } from '@confluence/page-utils/entry-points/enums';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';

import { errorContainsCodes, PERMISSION_ERROR_CODES } from '../detailsPanelUtils';
import { normalizeContentViewsData } from '../normalizers/normalizeContentViewsData';

import type {
	useContentViewsDetailsPanelContentViewsQuery as useContentViewsDetailsPanelContentViewsQuery$data,
	useContentViewsDetailsPanelContentViewsQueryVariables as useContentViewsDetailsPanelContentViewsQuery$variables,
} from './__types__/useContentViewsDetailsPanelContentViewsQuery';

const useContentViewsDetailsPanelContentViewsQuery = gql`
	query useContentViewsDetailsPanelContentViewsQuery($contentId: ID!, $isDraft: Boolean!) {
		contentAnalyticsViewers(contentId: $contentId) @skip(if: $isDraft) {
			count
		}
		user(current: true) {
			id
			confluence {
				permissionType
			}
		}
	}
`;
export const useContentViews = () => {
	const [contentId] = usePageContentId();

	const {
		contentStatus,
		loading: isContentStatusLoading,
		error: contentStatusError,
		refetch: refetchContentStatus,
	} = useContentStatus();

	const { data, loading, error, refetch } = useQuery<
		useContentViewsDetailsPanelContentViewsQuery$data,
		useContentViewsDetailsPanelContentViewsQuery$variables
	>(useContentViewsDetailsPanelContentViewsQuery, {
		variables: {
			contentId: contentId ?? '',
			isDraft: contentStatus === ContentStatus.DRAFT,
		},
		skip: isContentStatusLoading || !!contentStatusError || !contentStatus || !contentId,
	});
	const isPermissionError = error && errorContainsCodes(error, PERMISSION_ERROR_CODES);

	const normalizedData = useMemo(() => normalizeContentViewsData(data), [data]);
	const { count, isInternalUser, isSSRError } = normalizedData;
	const contentViewsError = contentStatusError || error;

	return {
		data: normalizedData,
		loading,
		error: contentViewsError,
		refetch: !!contentStatusError ? refetchContentStatus : refetch,
		shouldHideViewsAnalytics: Boolean(
			isPermissionError || !isInternalUser || isSSRError || !count || contentViewsError || loading,
		),
	};
};
